import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Paper, Tab, Tabs, TextField, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import DataTable from '../../../components/design/DataTable';
import LoadingState from '../../../components/design/LoadingState';
import EmptyState from '../../../components/design/EmptyState';
import campaignsService from '../../../services/campaignsService';

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ mt: 2 }}>{children}</Box>;
}

function parseNumbers(text) {
  return String(text || '')
    .split(/\r?\n/)
    .map(l => l.trim())
    .filter(Boolean);
}

function extractNumbersFromText(text) {
  const matches = String(text || '').match(/\d{8,15}/g) || [];
  return matches.map(s => s.replace(/\D/g, '')).filter(Boolean);
}

export default function CampaignsPage() {
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', description: '' });
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', description: '' });

  const [messageText, setMessageText] = useState('');
  const [recipientsText, setRecipientsText] = useState('');
  const [importing, setImporting] = useState(false);

  const selected = useMemo(() => campaigns.find(c => c.id === selectedId) || null, [campaigns, selectedId]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await campaignsService.list();
      const list = data?.campaigns || data?.data || data || [];
      setCampaigns(Array.isArray(list) ? list : []);
    } catch {
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (id) => {
    setLoading(true);
    try {
      const data = await campaignsService.get(id);
      setDetail(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load().catch(() => {}); }, []);

  useEffect(() => {
    if (!selectedId) return;
    loadDetail(selectedId).catch(() => {});
  }, [selectedId]);

  const isEditable = (c) => {
    const s = String(c?.status || '').toLowerCase();
    return s === 'draft' || s === 'paused' || s === 'scheduled';
  };

  const createCampaign = async () => {
    setLoading(true);
    try {
      const data = await campaignsService.create({ name: createForm.name, description: createForm.description || null });
      const createdId =
        data?.campaign?.id ??
        data?.data?.campaign?.id ??
        data?.data?.id ??
        data?.id ??
        null;
      setCreateOpen(false);
      setCreateForm({ name: '', description: '' });
      await load();
      if (createdId) setSelectedId(createdId);
      else toast.error('Campanha criada, mas o backend não retornou o ID.');
    } catch (e) {
      toast.error(e?.message || 'Erro ao criar campanha');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = () => {
    if (!selected) return;
    setEditForm({ name: selected.name || '', description: selected.description || '' });
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      await campaignsService.update(selectedId, { name: editForm.name, description: editForm.description || null });
      toast.success('Campanha atualizada');
      setEditOpen(false);
      await load();
      await loadDetail(selectedId);
    } catch (e) {
      toast.error(e?.message || 'Erro ao atualizar campanha');
    } finally {
      setLoading(false);
    }
  };

  const removeCampaign = async () => {
    if (!selectedId) return;
    if (!window.confirm('Excluir esta campanha?')) return;
    setLoading(true);
    try {
      await campaignsService.remove(selectedId);
      toast.success('Campanha excluída');
      setSelectedId(null);
      setDetail(null);
      await load();
    } catch (e) {
      toast.error(e?.message || 'Erro ao excluir campanha');
    } finally {
      setLoading(false);
    }
  };

  const addMessage = async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      await campaignsService.addMessage(selectedId, { text: messageText });
      setMessageText('');
      await loadDetail(selectedId);
    } finally {
      setLoading(false);
    }
  };

  const addRecipients = async () => {
    if (!selectedId) return;
    const numbers = parseNumbers(recipientsText);
    if (numbers.length === 0) return;
    setLoading(true);
    try {
      await campaignsService.addRecipients(selectedId, { numbers });
      setRecipientsText('');
      await loadDetail(selectedId);
    } finally {
      setLoading(false);
    }
  };

  const importRecipientsFile = async (file) => {
    if (!selectedId) return;
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      const extracted = extractNumbersFromText(text);
      const unique = [...new Set(extracted)];
      if (unique.length === 0) {
        toast.error('Não encontrei números no arquivo.');
        return;
      }
      await campaignsService.addRecipients(selectedId, { numbers: unique });
      toast.success(`Importado: ${unique.length} número(s).`);
      await loadDetail(selectedId);
    } catch (e) {
      toast.error(e?.message || 'Erro ao importar arquivo');
    } finally {
      setImporting(false);
    }
  };

  const start = async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      await campaignsService.start(selectedId);
      await load();
      await loadDetail(selectedId);
      toast.success('Campanha iniciada');
    } catch (e) {
      toast.error(e?.message || 'Erro ao iniciar campanha');
    } finally {
      setLoading(false);
    }
  };

  const pause = async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      await campaignsService.pause(selectedId);
      await load();
      await loadDetail(selectedId);
      toast.success('Campanha pausada');
    } catch (e) {
      toast.error(e?.message || 'Erro ao pausar campanha');
    } finally {
      setLoading(false);
    }
  };

  const cancel = async () => {
    if (!selectedId) return;
    if (!window.confirm('Cancelar esta campanha?')) return;
    setLoading(true);
    try {
      await campaignsService.cancel(selectedId);
      await load();
      await loadDetail(selectedId);
      toast.success('Campanha cancelada');
    } catch (e) {
      toast.error(e?.message || 'Erro ao cancelar campanha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Campanhas</Typography>
        <Button variant="outlined" onClick={() => setCreateOpen(true)}>Nova campanha</Button>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2 }}>
            {loading && <LoadingState />}
            {!loading && campaigns.length === 0 && <EmptyState title="Nenhuma campanha" />}
            {!loading && campaigns.length > 0 && (
              <DataTable
                columns={[
                  { key: 'name', label: 'Nome' },
                  { key: 'status', label: 'Status' }
                ]}
                rows={campaigns}
                getKey={(r) => r.id}
                onRowClick={(row) => setSelectedId(row.id)}
              />
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2 }}>
            {!selected && <EmptyState title="Selecione uma campanha" />}
            {selected && (
              <>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Box>
                    <Typography variant="h6">{selected.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selected.description || ''}{selected.status ? ` · ${selected.status}` : ''}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={1}>
                    {isEditable(selected) ? (
                      <Button variant="outlined" onClick={openEdit} disabled={loading}>Editar</Button>
                    ) : null}
                    <Button variant="outlined" onClick={start} disabled={loading || !['draft', 'paused', 'scheduled'].includes(String(selected.status || '').toLowerCase())}>
                      {String(selected.status || '').toLowerCase() === 'paused' ? 'Retomar' : 'Iniciar'}
                    </Button>
                    <Button variant="outlined" onClick={pause} disabled={loading || String(selected.status || '').toLowerCase() !== 'running'}>Pausar</Button>
                    <Button variant="outlined" color="error" onClick={cancel} disabled={loading || ['cancelled', 'finished'].includes(String(selected.status || '').toLowerCase())}>Cancelar</Button>
                    <Button variant="contained" color="error" onClick={removeCampaign} disabled={loading || String(selected.status || '').toLowerCase() === 'running'}>Excluir</Button>
                  </Box>
                </Box>

                <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile>
                  <Tab label="Configuração" />
                  <Tab label="Envio" />
                </Tabs>

                <TabPanel value={tab} index={0}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Mensagem</Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    placeholder="Digite a mensagem da campanha"
                    disabled={loading || !isEditable(selected)}
                  />
                  <Box mt={1} display="flex" justifyContent="flex-end">
                    <Button onClick={addMessage} disabled={loading || !isEditable(selected) || !messageText.trim()}>Adicionar</Button>
                  </Box>

                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 3, mb: 1 }}>Destinatários</Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={5}
                    value={recipientsText}
                    onChange={(e) => setRecipientsText(e.target.value)}
                    placeholder="Um número por linha (somente dígitos ou com formatação)"
                    disabled={loading || importing || !isEditable(selected)}
                  />
                  <Box mt={1} display="flex" justifyContent="space-between" alignItems="center">
                    <Button
                      variant="outlined"
                      component="label"
                      disabled={loading || importing || !selectedId || !isEditable(selected)}
                    >
                      Importar CSV/TXT
                      <input
                        type="file"
                        hidden
                        accept=".csv,.txt"
                        onChange={(e) => {
                          const f = e.target.files && e.target.files[0];
                          e.target.value = '';
                          importRecipientsFile(f).catch(() => {});
                        }}
                      />
                    </Button>
                    <Button onClick={addRecipients} disabled={loading || importing || !isEditable(selected) || parseNumbers(recipientsText).length === 0}>Adicionar</Button>
                  </Box>
                </TabPanel>

                <TabPanel value={tab} index={1}>
                  {!detail ? <LoadingState /> : (
                    <>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Resumo</Typography>
                      <Grid container spacing={1}>
                        {Object.entries(detail.counts || {}).map(([k, v]) => (
                          <Grid item xs={6} md={3} key={k}>
                            <Paper sx={{ p: 1 }}>
                              <Typography variant="caption" color="text.secondary">{k}</Typography>
                              <Typography variant="h6">{v}</Typography>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mt: 2, mb: 1 }}>Mensagens</Typography>
                      {detail.messages?.length ? (
                        <DataTable
                          columns={[
                            { key: 'orderIndex', label: 'Ordem' },
                            { key: 'templateId', label: 'Template' },
                            { key: 'content', label: 'Conteúdo', render: (v) => (v?.body || JSON.stringify(v || {})) }
                          ]}
                          rows={detail.messages}
                          getKey={(r) => r.id}
                        />
                      ) : (
                        <EmptyState title="Sem mensagens" />
                      )}
                    </>
                  )}
                </TabPanel>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Nova campanha</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nome"
            value={createForm.name}
            onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
            sx={{ mt: 1 }}
          />
          <TextField
            fullWidth
            label="Descrição"
            value={createForm.description}
            onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setCreateOpen(false)}>Fechar</Button>
          <Button onClick={createCampaign} disabled={!createForm.name.trim() || loading}>Criar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Editar campanha</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nome"
            value={editForm.name}
            onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
            sx={{ mt: 1 }}
          />
          <TextField
            fullWidth
            label="Descrição"
            value={editForm.description}
            onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setEditOpen(false)}>Fechar</Button>
          <Button onClick={saveEdit} disabled={!editForm.name.trim() || loading}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
